import React, { useMemo } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   BUDGET STRUCTURE PANEL (A2)
   ----------------------------------------------------------------------------
   RÔLE
   - panneau latéral de lecture globale de la structuration budgétaire
   - donne une vision synthétique du site en cours de structuration
   - ne dépend pas du clic sur une carte
   - reste sobre, compact et cohérent avec la grammaire Corelia

   ADAPTATION
   - structure conservée
   - durcissement léger pour compatibilité avec la colonne scrollable
============================================================================ */

/* ============================================================================
   TOKENS LOCAUX
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

/* ============================================================================
   STYLES PARTAGÉS LOCAUX
============================================================================ */

const S = {
  panel: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 22,
    boxSizing: "border-box",
  },

  panelHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minHeight: 12,
    minWidth: 0,
    flex: "0 0 auto",
  },

  panelTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.02rem",
    lineHeight: 1.1,
    color: T.text,
    fontWeight: 600,
  },

  block: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingBottom: 2,
    minWidth: 0,
    flex: "0 0 auto",
  },

  blockTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.84rem",
    lineHeight: 1.12,
    color: theme?.colors?.text?.caneva || "#c24c2c",
    fontWeight: 600,
  },

  rows: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 0,
  },

  kvRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  kvLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.74rem",
    lineHeight: 1.2,
    color: T.soft,
    minWidth: 0,
  },

  kvValue: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem",
    lineHeight: 1.2,
    fontWeight: 600,
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  empty: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 22,
    boxSizing: "border-box",
  },

  emptyText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.78rem",
    lineHeight: 1.35,
    color: T.verySoft,
  },
};

/* ============================================================================
   MOCK GLOBAL DATA
============================================================================ */

const MOCK_BUDGET_OVERVIEW = {
  totalPackages: 10,
  structuredPackages: 6,
  totalItems: 24,

  totalZones: 6,
  coveredZones: 4,
  uncoveredZones: 2,

  readyCount: 3,
  toStructureCount: 4,
  upstreamReviewCount: 3,

  qtyCount: 5,
  breakdownCount: 4,
  lumpSumCount: 1,

  remainingPackages: 4,
  remainingZones: 2,
  criticalPackages: 3,

  estimationReadiness: "Partial",
  mappingReadiness: "To consolidate",
  projectExposure: "Watch required",
};

/* ============================================================================
   HELPERS
============================================================================ */

const getSignalColor = (value) => {
  switch (value) {
    case "Ready":
    case "Structured":
    case "Available":
      return T.greenText;

    case "Partial":
    case "To structure":
    case "To consolidate":
    case "Watch required":
      return T.yellowText;

    case "Critical":
    case "Upstream review":
    case "Blocked":
      return T.redText;

    default:
      return T.greyText;
  }
};

const computeMetrics = (data) => {
  if (!data) return null;

  const coverageRate =
    data.totalPackages > 0
      ? `${Math.round((data.structuredPackages / data.totalPackages) * 100)}%`
      : "0%";

  const zoneCoverageRate =
    data.totalZones > 0
      ? `${Math.round((data.coveredZones / data.totalZones) * 100)}%`
      : "0%";

  return {
    coverageRate,
    zoneCoverageRate,
    readyTone: getSignalColor("Ready"),
    toStructureTone: getSignalColor("To structure"),
    upstreamTone: getSignalColor("Upstream review"),
    estimationTone: getSignalColor(data.estimationReadiness),
    mappingTone: getSignalColor(data.mappingReadiness),
    exposureTone: getSignalColor(data.projectExposure),
  };
};

/* ============================================================================
   COMPOSANTS STANDARD
============================================================================ */

const PanelBlock = ({ title, children }) => (
  <div style={S.block}>
    <div style={S.blockTitle}>{title}</div>
    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text }) => (
  <div style={S.kvRow}>
    <div style={S.kvLabel}>{label}</div>
    <div style={{ ...S.kvValue, color: valueColor }}>{value}</div>
  </div>
);

const EmptyState = () => (
  <div style={S.empty}>
    <div style={S.panelHeader}>
      <div style={S.panelTitle}>Structure overview</div>
    </div>

    <PanelBlock title="Global status">
      <div style={S.emptyText}>
        La vue de synthèse s’affichera ici dès qu’une structure budgétaire
        globale sera disponible.
      </div>
    </PanelBlock>
  </div>
);

/* ============================================================================
   MAIN COMPONENT
============================================================================ */

const BudgetStructurePanel = ({
  overviewData = null,
  useMock = true,
}) => {
  const data = overviewData || (useMock ? MOCK_BUDGET_OVERVIEW : null);

  const metrics = useMemo(() => computeMetrics(data), [data]);

  if (!data || !metrics) return <EmptyState />;

  return (
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <div style={S.panelTitle}>Structure overview</div>
      </div>

      <PanelBlock title="Global status">
        <div style={S.rows}>
          <KeyValueRow
            label="Structured packages"
            value={`${data.structuredPackages} / ${data.totalPackages}`}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Coverage rate"
            value={metrics.coverageRate}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Integrated items"
            value={data.totalItems}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Site coverage">
        <div style={S.rows}>
          <KeyValueRow
            label="Covered zones"
            value={`${data.coveredZones} / ${data.totalZones}`}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Zone coverage"
            value={metrics.zoneCoverageRate}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Uncovered zones"
            value={data.uncoveredZones}
            valueColor={data.uncoveredZones > 0 ? T.yellowText : T.greenText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Structure logic">
        <div style={S.rows}>
          <KeyValueRow
            label="QTY packages"
            value={data.qtyCount}
            valueColor={T.greenText}
          />
          <KeyValueRow
            label="Breakdown packages"
            value={data.breakdownCount}
            valueColor={T.yellowText}
          />
          <KeyValueRow
            label="Lump sum packages"
            value={data.lumpSumCount}
            valueColor={T.redText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Maturity status">
        <div style={S.rows}>
          <KeyValueRow
            label="Ready"
            value={data.readyCount}
            valueColor={metrics.readyTone}
          />
          <KeyValueRow
            label="To structure"
            value={data.toStructureCount}
            valueColor={metrics.toStructureTone}
          />
          <KeyValueRow
            label="Upstream review"
            value={data.upstreamReviewCount}
            valueColor={metrics.upstreamTone}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Remaining workload">
        <div style={S.rows}>
          <KeyValueRow
            label="Packages to integrate"
            value={data.remainingPackages}
            valueColor={data.remainingPackages > 0 ? T.yellowText : T.greenText}
          />
          <KeyValueRow
            label="Zones to cover"
            value={data.remainingZones}
            valueColor={data.remainingZones > 0 ? T.yellowText : T.greenText}
          />
          <KeyValueRow
            label="Critical packages"
            value={data.criticalPackages}
            valueColor={data.criticalPackages > 0 ? T.redText : T.greenText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Project impacts">
        <div style={S.rows}>
          <KeyValueRow
            label="Estimation readiness"
            value={data.estimationReadiness}
            valueColor={metrics.estimationTone}
          />
          <KeyValueRow
            label="Mapping readiness"
            value={data.mappingReadiness}
            valueColor={metrics.mappingTone}
          />
          <KeyValueRow
            label="Project exposure"
            value={data.projectExposure}
            valueColor={metrics.exposureTone}
          />
        </div>
      </PanelBlock>
    </div>
  );
};

export default BudgetStructurePanel;